import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../config/firebase';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function login(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    // Fetch user profile from Firestore
    const profileDoc = await getDoc(doc(db, 'users', result.user.uid));
    if (profileDoc.exists()) {
      setUserProfile(profileDoc.data());
    }
    return result;
  }

  async function logout() {
    setUserProfile(null);
    return signOut(auth);
  }

  // Create a new user (for initial setup or adding daughter's account)
  async function createUser(email, password, displayName, role = 'admin') {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const profile = {
      email,
      displayName,
      role,
      createdAt: new Date().toISOString(),
    };
    await setDoc(doc(db, 'users', result.user.uid), profile);
    setUserProfile(profile);
    return result;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const profileDoc = await getDoc(doc(db, 'users', user.uid));
          if (profileDoc.exists()) {
            setUserProfile(profileDoc.data());
          } else {
            // First-time user: create a basic profile
            const profile = {
              email: user.email,
              displayName: user.email.split('@')[0],
              role: 'admin',
              createdAt: new Date().toISOString(),
            };
            await setDoc(doc(db, 'users', user.uid), profile);
            setUserProfile(profile);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    logout,
    createUser,
    loading,
    displayName: userProfile?.displayName || currentUser?.email?.split('@')[0] || 'User',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
